import "../k";
import { addButton } from "./button";
import { QUIZ_GREEN } from "../util/style";

const FRAME_WIDTH = 380;
const FRAME_HEIGHT = 200;

function addAudioPlayer(soundName: string, isReal: boolean, comps?: any[]) {
  // Container to hold both frame and external play button
  const container = add([
    pos(),
    anchor("center"),
    { isEnabled: true, isPlaying: false, soundHandle: null as any },
    ...comps,
  ]);

  // The clickable frame with custom dimensions
  const frame = container.add([
    sprite("frame", { width: FRAME_WIDTH, height: FRAME_HEIGHT }),
    anchor("center"),
    animate(),
    area(),
    scale(1),
    color(255, 255, 255),
    pos(0, -50),
    z(1),
  ]);

  // Waveform bars (inside frame)
  const NUM_BARS = 9;
  const BAR_WIDTH = 24;
  const BAR_GAP = 8;
  const bars: any[] = [];

  for (let i = 0; i < NUM_BARS; i++) {
    const xOffset = (i - (NUM_BARS - 1) / 2) * (BAR_WIDTH + BAR_GAP);
    const bar = frame.add([
      rect(BAR_WIDTH, 40, {radius: 4}),
      anchor("bot"),
      color(QUIZ_GREEN),
      z(2),
      pos(xOffset, 30),
    ]);
    bars.push(bar);
  }

  // Progress text (inside frame)
  const progressText = frame.add([
    text("0:00 / 0:00", { size: 22, font: "rumeur" }),
    anchor("center"),
    color(BLACK),
    z(2),
    pos(0, 52),
  ]);

  // Play button OUTSIDE the frame (below it)
  const BUTTON_Y = FRAME_HEIGHT / 2 - 50 + 60;
  const playButton = addButton("button", [z(10)], vec2(102, 102));

  const ICON_COLOR = BLACK;

  const iconOffset = playButton.add([
    pos(0, -4),
  ]);

  const playIcon = iconOffset.add([
    polygon([vec2(-10, -14), vec2(-10, 14), vec2(14, 0)]),
    anchor("center"),
    color(ICON_COLOR),
    z(11),
  ]);

  const pauseIcon1 = iconOffset.add([
    rect(8, 28),
    anchor("center"),
    color(ICON_COLOR),
    z(11),
    pos(-8, 0),
  ]);
  pauseIcon1.hidden = true;

  const pauseIcon2 = iconOffset.add([
    rect(8, 28),
    anchor("center"),
    color(ICON_COLOR),
    z(11),
    pos(8, 0),
  ]);
  pauseIcon2.hidden = true;

  // Cleanup button when container is destroyed
  container.onDestroy(() => {
    playButton.destroy();
  });

  frame.animate("scale", [vec2(0.8, 0.8), vec2(1, 1)], {
    duration: 0.5,
    easing: easings.easeOutElastic,
    loops: 1,
  });

  let isAnticipating = false;

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, "0")}`;
  };

  const togglePlayback = () => {
    if (!container.soundHandle) {
      container.soundHandle = play(soundName, { volume: 0.7, paused: false });
      container.isPlaying = true;
    } else if (container.isPlaying) {
      container.soundHandle.paused = true;
      container.isPlaying = false;
    } else {
      container.soundHandle.paused = false;
      container.isPlaying = true;
    }
    updateIcons();
  };

  const updateIcons = () => {
    playIcon.hidden = container.isPlaying;
    pauseIcon1.hidden = !container.isPlaying;
    pauseIcon2.hidden = !container.isPlaying;
  };

  // Click on play button toggles playback (doesn't trigger selection)
  playButton.onClick(() => {
    if (container.isEnabled) {
      togglePlayback();
    }
  });

  // Update waveform animation and progress
  container.onUpdate(() => {
    // Sync button state and position with container
    playButton.isEnabled = container.isEnabled;
    playButton.pos = container.pos.add(vec2(0, BUTTON_Y));

    // Animate bars
    for (let i = 0; i < bars.length; i++) {
      const baseHeight = 40;
      if (container.isPlaying) {
        const phase = i * 0.8;
        const wave = Math.sin(time() * 5 + phase) * 0.5 + 0.5;
        const wave2 = Math.sin(time() * 7.3 + phase * 1.5) * 0.3;
        bars[i].height = baseHeight + wave * 40 + wave2 * 20;
      } else {
        bars[i].height = lerp(bars[i].height, baseHeight, dt() * 5);
      }
    }

    // Update progress text
    if (container.soundHandle) {
      const currentTime = container.soundHandle.time ? container.soundHandle.time() : 0;
      const duration = container.soundHandle.duration ? container.soundHandle.duration() : 0;
      progressText.text = `${formatTime(currentTime)} / ${formatTime(duration)}`;

      // Check if sound ended
      if (currentTime >= duration && duration > 0) {
        container.isPlaying = false;
        updateIcons();
      }
    }
  });

  // Hover effects on the frame (selection area)
  frame.onHoverUpdate(() => {
    if (!container.isEnabled) {
      frame.scale = vec2(1, 1);
      frame.color = Color.WHITE;
      setCursor("default");
      isAnticipating = false;
      return;
    }
    const t = time() * 10;
    const pulse = Math.sin(t) * 10 + 230;
    frame.scale = vec2(1.05);
    frame.color = rgb(pulse, pulse, pulse);
    setCursor("pointer");
    if (!isAnticipating) {
      play("rollover", { volume: 0.2 });
      isAnticipating = true;
    }
  });

  frame.onHoverEnd(() => {
    frame.scale = vec2(1, 1);
    frame.color = Color.WHITE;
    setCursor("default");
    isAnticipating = false;
  });

  // Clicking the frame triggers selection
  frame.onClick(() => {
    if (container.isEnabled) {
      play("click", { volume: 0.5 });
    }
  });

  // Method to stop audio when question is answered
  container.stop = () => {
    if (container.soundHandle) {
      container.soundHandle.stop();
      container.soundHandle = null;
    }
    container.isPlaying = false;
    updateIcons();
  };

  // Expose frame's onClick for the quiz scene to attach handlers
  container.onClick = (fn: () => void) => {
    frame.onClick(fn);
  };

  return container;
}

export { addAudioPlayer };
